import { ActionReducer } from "@ngrx/store";
import { QuoteState } from "../quote.state";
import { PagesStore } from "../../pages.state";
import { UserState } from "../../../user/user.state";
import { validateQuoteForOrder } from "./validate-quote";

// **************************************************************************
// VALIDATION META-REDUCER
// **************************************************************************

interface StateWithPages {
    pages?: PagesStore;
    user?: UserState;
    [key: string]: unknown;
}

/**
 * Meta-reducer that recalculates quote validation whenever quote state changes.
 * Runs after regular reducers, updating validation in state automatically.
 */
export function validationMetaReducer(
    reducer: ActionReducer<StateWithPages>
): ActionReducer<StateWithPages> {
    return (state, action) => {
        const nextState = reducer(state, action);

        if (!nextState?.pages?.quote) {
            return nextState;
        }

        const prevCompanyId = state?.user?.selectedCompany?.id;
        const nextCompanyId = nextState.user?.selectedCompany?.id;

        // Skip if neither the quote nor the selected company changed
        if (!hasQuoteChanged(state?.pages?.quote, nextState.pages.quote)
            && prevCompanyId === nextCompanyId) {
            return nextState;
        }

        // Find selected version
        const quote = nextState.pages.quote;
        const version = quote.versions?.find(
            v => v.id === quote.selectedVersion
        );

        // Recalculate validation
        const validation = validateQuoteForOrder(quote, version, nextCompanyId);

        return {
            ...nextState,
            pages: {
                ...nextState.pages,
                quote: {
                    ...nextState.pages.quote,
                    validation,
                },
            },
        };
    };
}

/**
 * Checks if relevant quote data has changed (excludes validation itself).
 */
function hasQuoteChanged(prev: QuoteState | undefined, next: QuoteState): boolean {
    if (!prev) return true;

    // Compare only fields that affect validation
    return (
        prev.customer !== next.customer ||
        prev.deliveryInfo !== next.deliveryInfo ||
        prev.salesInfo !== next.salesInfo ||
        prev.leasingInfo !== next.leasingInfo ||
        prev.versions !== next.versions ||
        prev.selectedVersion !== next.selectedVersion ||
        prev.creditCheck !== next.creditCheck
    );
}
